import React, {useRef, useEffect, useState} from "react";
import FormField from "../../forms/form-field";
import BaseModal from "../../modals/base-modal";
import FormSelect from "../../forms/form-select";
import Tooltip from "../../tooltip/tooltip";
import { instanceMob } from "../../../utils/utils";
import InlineModal from "../../info-modal/infoModal";
import { getBackendError } from "../../../utils/error-helpers";
import CreateGenericItemModal from "./create-generic-item-modal";
import useLocationStore from "../config/use-location-store";
import CreateProviderModal from "./create-provider-modal";
import {useNavigate} from "react-router-dom";

const ProductRegistrationModal = ({ show, onClose, productData = null }) => {
    const navigate = useNavigate();
    const formRef = useRef(null);
    const [infoModal, setInfoModal] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [unitPrice, setUnitPrice] = useState(0);
    const [quantity, setQuantity] = useState("");
    const [totalPurchasePrice, setTotalPurchasePrice] = useState("");
    const [salePrice, setSalePrice] = useState("");
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [providers, setProviders] = useState([]);
    const [nit, setNit] = useState("");
    const [showCreateProviderModal, setShowCreateProviderModal] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("");
    const [showCreateItemModal, setShowCreateItemModal] = useState(false);
    const [itemTypeToCreate, setItemTypeToCreate] = useState(null);

    const [paidValue, setPaidValue] = useState(0);
    const [pendingValue, setPendingValue] = useState(0);
    const [newQuantity, setNewQuantity] = useState("");

    const isEditing = Boolean(productData);
    const [fieldsDisabled, setFieldsDisabled] = useState(isEditing);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isBlocked, setIsBlocked] = useState(false);

    const handleCategoryChange = (e) => setSelectedCategory(Array.isArray(e.target.value) ? e.target.value : [e.target.value]);
    const handleBrandChange = (e) => setSelectedBrand(e.target.value);

    const selectedLocation = useLocationStore((state) => state.selectedLocation);

    const handleOpenCreateCategoryModal = () => {
        setItemTypeToCreate("CATEGORY");
        setShowCreateItemModal(true);
    };

    const handleOpenCreateBrandModal = () => {
        setItemTypeToCreate("BRAND");
        setShowCreateItemModal(true);
    };

    const handleCloseCreateItemModal = (newItem) => {
        setShowCreateItemModal(false);
        setItemTypeToCreate(null);

        if (newItem) {
            let newOptionValue = null;

            if (newItem.category_id) {
                const formatted = { value: String(newItem.category_id), label: newItem.name };
                setCategories((prev) => [...prev, formatted]);
                newOptionValue = formatted.value;
                const currentCategories = selectedCategory || [];
                const newSelectedCategories = [...currentCategories, newOptionValue];
                setSelectedCategory(newSelectedCategories);

            } else if (newItem.brand_id) {
                const formatted = { value: String(newItem.brand_id), label: newItem.name };
                setBrands((prev) => [...prev, formatted]);
                newOptionValue = formatted.value;
                setSelectedBrand(newOptionValue);
            }
        } else {
            fetchCategoriesBrandsAndProviders();
        }
    };

    const cleanCurrencyInput = (value) => {
        const cleaned = value.replace(/[^0-9.]/g, "");
        const parts = cleaned.split(".");
        if (parts.length > 2) return parts[0] + "." + parts.slice(1).join("");
        return cleaned;
    };

    const handleUnitPriceChange = (e) => {
        const value = cleanCurrencyInput(e.target.value);
        if (value.startsWith("-")) return;

        setUnitPrice(value);

        if (value === "" || Number(value) === 0) {
            setTotalPurchasePrice("0");
            setPaidValue("0");
            setPendingValue("0");

            const form = formRef.current;
            if (form) {
                if (form.elements["total_purchase_price"]) form.elements["total_purchase_price"].value = "";
                if (form.elements["value_paid"]) form.elements["value_paid"].value = "";
                if (form.elements["value_pending"]) form.elements["value_pending"].value = "";
            }
        }
    };

    const handleQuantityChange = (e) => {
        const value = e.target.value.replace(/[^0-9]/g, "");
        if (value.length > 1 && value.startsWith("0")) return;
        setQuantity(value);
    };

    const handleQuantityBlur = () => {
        const numericQuantity = Number(quantity);
        const originalQty = Number(productData?.quantity || 0);

        if (!isEditing) return;

        if (numericQuantity <= 0) {
            setQuantity(originalQty.toString());
            setFieldsDisabled(true);
            setUnitPrice(productData.unit_price?.toString() || "");
            setTotalPurchasePrice(productData.total_purchase_price?.toString() || "");
            setPaidValue(productData.value_paid || 0);
            setPendingValue(productData.value_pending || 0);
            setPaymentMethod(productData.payment_method || "");
            return;
        }

        const newTotalQty = originalQty + numericQuantity;
        setQuantity(newTotalQty.toString());

        setFieldsDisabled(false);

        setUnitPrice("0");
        setTotalPurchasePrice("0");
        setPaidValue("0");
        setPendingValue("0");
        setPaymentMethod("");

        const form = formRef.current;
        if (form) {
            if (form.elements["unit_price"]) form.elements["unit_price"].value = "";
            if (form.elements["total_purchase_price"]) form.elements["total_purchase_price"].value = "";
            if (form.elements["value_paid"]) form.elements["value_paid"].value = "0";
            if (form.elements["value_pending"]) form.elements["value_pending"].value = "0";
            if (form.elements["payment_method"]) form.elements["payment_method"].value = "";
            if (form.elements["other_payment_method_display"]) form.elements["other_payment_method_display"].value = "";
            if (form.elements["other_payment_method"]) form.elements["other_payment_method"].value = "";
        }
    };

    const handleNewQuantityChange = (e) => {
        const value = e.target.value.replace(/[^0-9]/g, "");
        if (value.length > 1 && value.startsWith("0")) return;
        setNewQuantity(value);
    };

    const handleNewQuantityBlur = () => {
        const numericNewQuantity = Number(newQuantity);
        const originalQty = Number(productData?.quantity || 0);

        if (!isEditing) return;

        if (numericNewQuantity <= 0) {
            setFieldsDisabled(true);
            setUnitPrice(productData.unit_price?.toString() || "");
            setTotalPurchasePrice(productData.total_purchase_price?.toString() || "");
            setPaidValue(productData.value_paid || 0);
            setPendingValue(productData.value_pending || 0);
            setPaymentMethod(productData.payment_method || "");
            return;
        }

        const newTotalQty = originalQty + numericNewQuantity;
        setQuantity(newTotalQty.toString());
        setFieldsDisabled(false);

        setUnitPrice("0");
        setTotalPurchasePrice("0");
        setPaidValue("0");
        setPendingValue("0");
        setPaymentMethod("");
    };

    const handleSalePriceChange = (e) => {
        const value = cleanCurrencyInput(e.target.value);
        if (value.startsWith("-")) return;
        setSalePrice(value);
    };

    const handleOpenCreateProviderModal = () => {
        setShowCreateProviderModal(true);
    };

    const handleProviderChange = (e) => {
        const selectedValue = e.target.value;
        setSelectedProvider(selectedValue);

        const selected = providers.find((p) => p.value === selectedValue);
        setNit(selected?.nit || "");
    };

    const handlePaymentChange = (e) => {
        const val = e?.target?.value ?? e;
        const num = Number(val);
        setPaymentMethod(num);

        const form = formRef.current;
        if (form && num !== 50) {
            if (form.elements["other_payment_method_display"]) form.elements["other_payment_method_display"].value = "";
            if (form.elements["other_payment_method"]) form.elements["other_payment_method"].value = "";
        }
    };

    const handleOtherPaymentDisplayChange = (e) => {
        const form = formRef.current;
        if (!form) return;
        const hidden = form.elements["other_payment_method"];
        if (hidden) hidden.value = e.target.value;
    };

    const handlePaidChange = (val) => {
        const numeric = Number(val) || 0;
        const total = Number(totalPurchasePrice) || 0;

        if (!total || total <= 0) {
            setInfoModal({
                iconType: "warning",
                title: "Total no definido",
                description: "Primero debes ingresar el precio de compra por unidad antes de registrar un valor pagado.",
                secondaryAction: {
                    label: "Cerrar",
                    onClick: () => {
                        setInfoModal(null);
                        setIsBlocked(false);
                    }
                },
            });
            setIsBlocked(true);

            setPaidValue("0");
            setPendingValue("0");
            return;
        }

        if (numeric > total) {
            setInfoModal({
                iconType: "warning",
                title: "Valor pagado inválido",
                description: "El valor pagado no puede superar el precio total de la compra.",
                secondaryAction: {
                    label: "Cerrar",
                    onClick: () => {
                        setInfoModal(null);
                        setIsBlocked(false);
                    }
                },
            });
            setIsBlocked(true);

            setPaidValue("0");
            setPendingValue("0");
            return;
        }

        setIsBlocked(false);
        setPaidValue(numeric.toString());
        setPendingValue((total - numeric).toFixed(2));
    }

    const formFields = [
        {
            Component: FormSelect,
            label: "Proovedor",
            name: "provider_id",
            options: providers,
            type: "text",
            placeholder: "Nombre de la empresa",
            searchable: true,
            isLoading,
            actionButton: handleOpenCreateProviderModal,
            required: true,
            value: selectedProvider || "",
            onChange: handleProviderChange,
            tooltip: "Selecciona el nombre del proveedor o empresa a la que se realizará el abono del pago pendiente registrado previamente."
        },
        {
            Component: FormField,
            label: "NIT",
            name: "nit",
            type: "text",
            placeholder: "automático",
            isLoading,
            required: true,
            readOnly: true,
            value: nit,
        },
        {
            Component: FormField,
            label: "ID del producto",
            name: "product_id",
            type: "text",
            placeholder: "Escribe aquí",
            isLoading,
            required: true,
            tooltip: "Código único que debes asignar al artículo para identificarlo en el inventario y facilitar su búsqueda y control.",
            value: productData?.id || undefined,
            readOnly: isEditing
        },
        {
            Component: FormField,
            label: "Nombre",
            name: "name",
            type: "text",
            placeholder: "Escribe aquí",
            isLoading,
            required: true,
            value: productData?.name  || undefined,
            tooltip: "Indica el nombre con el que identificarás el artículo en el inventario, asegurando que sea claro y fácil de reconocer.",
        },
        {
            Component: FormSelect,
            label: "Categoría",
            name: "category_ids",
            options: categories,
            value: selectedCategory || [],
            placeholder: "Selecciona la categoría o crea una",
            searchable: true,
            isLoading,
            actionButton: handleOpenCreateCategoryModal,
            required: true,
            onChange: handleCategoryChange,
            tooltip: "Selecciona la categoría a la que pertenece el producto para organizarlo en el inventario. Si no existe la categoría deseada, usa el botón \"Agregar categoría\" para crearla en el momento.",
            multiple: true
        },
        {
            Component: FormSelect,
            label: "Marca",
            name: "brand_id",
            options: brands,
            value: selectedBrand || null,
            placeholder: "Selecciona la marca",
            searchable: true,
            isLoading,
            actionButton: handleOpenCreateBrandModal,
            required: true,
            onChange: handleBrandChange,
            tooltip: "Indica la marca del producto para identificar su origen o fabricante. Si la marca no está registrada, utiliza el botón \"Agregar marca\" para crearla al instante.",
        },
        {
            Component: FormField,
            label: "Precio de venta por unidad",
            name: "sale_price_per_unit",
            type: "currency",
            placeholder: "Escribe aquí",
            isLoading,
            required: true,
            value: salePrice === "" ? undefined : salePrice,
            onChange: handleSalePriceChange,
            tooltip: "Valor al que se venderá cada unidad del producto al cliente final. Debe ser mayor o igual al Precio por unidad.",
        },
        {
            Component: FormField,
            label: "Stock mínimo",
            name: "min_stock",
            type: "number",
            placeholder: "Escribe aquí",
            isLoading,
            required: true,
            value: productData?.minStock || undefined,
            tooltip: "Cantidad mínima del producto que debe mantenerse en inventario antes de generar una alerta de reabastecimiento.",
        },
        {
            Component: FormField,
            label: "Cantidad actual",
            name: "quantity",
            type: "number",
            placeholder: "Escribe aquí",
            isLoading,
            required: true,
            value: quantity || 0,
            onChange: !isEditing ? handleQuantityChange : undefined,
            onBlur: !isEditing ? handleQuantityBlur : undefined,
            tooltip: isEditing
                ? "Cantidad actual disponible en el inventario. No editable durante la actualización."
                : "Número total de unidades compradas al proveedor para este producto. Debe ser un entero positivo.",
            readOnly: isEditing,
            disabled: isEditing
        },
        {
            Component: FormField,
            label: "Nueva cantidad",
            name: "new_quantity",
            type: isEditing ? "number" : "hidden",
            placeholder: "Escribe aquí",
            isLoading,
            required: isEditing,
            value: isEditing ? (newQuantity === "" ? undefined : newQuantity) : undefined,
            onChange: isEditing ? handleNewQuantityChange : undefined,
            onBlur: isEditing ? handleNewQuantityBlur : undefined,
            tooltip: "Cantidad adicional que deseas agregar al inventario actual. Esta cantidad se sumará a la existente.",
        },
        {
            Component: FormField,
            label: "Precio de compra por unidad",
            name: "unit_price",
            type: "currency",
            placeholder: "Escribe aquí",
            isLoading,
            required: true,
            value: unitPrice,
            onChange: handleUnitPriceChange,
            tooltip: "Monto pagado al distribuidor por cada unidad del producto adquirido. Se usa para calcular el costo total de la compra.",
            disabled: fieldsDisabled
        },
        {
            Component: FormField,
            label: "Precio total de la compra",
            name: "total_purchase_price",
            type: "currency",
            placeholder: "Valor automático",
            isLoading,
            required: true,
            readOnly: true,
            value: totalPurchasePrice === "" ? 0 : totalPurchasePrice,
            tooltip: "Monto resultante de multiplicar el precio por unidad por la cantidad adquirida al proveedor. Se calcula automáticamente y no es editable.",
            disabled: fieldsDisabled
        },
        {
            Component: FormField,
            label: "Valor pagado",
            name: "value_paid",
            type: "currency",
            placeholder: "Escribe aquí",
            isLoading,
            required: true,
            value: paidValue,
            onChange: (e) => handlePaidChange(e.target.value),
            tooltip: "Cantidad efectivamente entregada al proveedor.",
            disabled: fieldsDisabled
        },
        {
            Component: FormField,
            label: "Valor pendiente por pagar",
            name: "value_pending",
            type: "currency",
            placeholder: "valor automático",
            readOnly: true,
            isLoading,
            value: pendingValue || 0,
            tooltip: "Saldo restante si el pago no cubre el total de la compra.",
            disabled: fieldsDisabled
        },
        {
            Component: FormSelect,
            label: "Método de pago",
            name: "payment_method",
            options: [
                {value: 10, label: "Tarjeta de Crédito o Débito"},
                {value: 20, label: "Transferencia"},
                {value: 30, label: "Efectivo"},
                {value: 50, label: "Otros"},
            ],
            placeholder: "Selecciona una opción",
            onChange: handlePaymentChange,
            isLoading,
            required: true,
            value: paymentMethod || null,
            tooltip: "Selecciona cómo se realizará el pago: tarjeta, efectivo o transferencia bancaria.",
            disabled: fieldsDisabled
        },
        {
            Component: FormField,
            label: "Entidad",
            name: "other_payment_method",
            placeholder: "Escribe aquí",
            disabled: paymentMethod !== 50 || fieldsDisabled,
            onChange: handleOtherPaymentDisplayChange,
            isLoading,
            required: paymentMethod === 50,
            tooltip: "Especifica por qué plataforma se hizo la transferencia: Nequi, Daviplata, Efecty u otra entidad."
        },
    ];

    const fetchCategoriesBrandsAndProviders = () => {
        setIsLoading(true);

        const categoryPromise = instanceMob
            .get("admin/accounting/category/")
            .then((res) => res.data)
            .then((data) =>
                data?.map((c) => ({
                    value: String(c?.category_id),
                    label: c?.name,
                }))
            )
            .catch(() => []);

        const brandPromise = instanceMob
            .get("admin/accounting/brand/")
            .then((res) => res.data)
            .then((data) =>
                data?.map((b) => ({
                    value: String(b?.brand_id),
                    label: b?.name,
                }))
            )
            .catch(() => []);

        const providerPromise = instanceMob
            .get("admin/accounting/provider/", {
                params: { location: selectedLocation.id },
            })
            .then((res) => res.data)
            .then((data) =>
                data?.map((p) => ({
                    value: String(p?.provider_id),
                    label: p?.name,
                    nit: p?.nit,
                }))
            )
            .catch((err) => {
                console.error("Error cargando proveedores:", err);
                return [];
            });

        Promise.all([categoryPromise, brandPromise, providerPromise])
            .then(([categoriesData, brandsData, providersData]) => {
                setCategories(categoriesData);
                setBrands(brandsData);
                setProviders(providersData);
            })
            .finally(() => setIsLoading(false));
    };

    const handleCloseCreateProviderModal = (newProvider) => {
        setShowCreateProviderModal(false);

        if (newProvider?.provider_id) {
            const formatted = {
                value: String(newProvider.provider_id),
                label: newProvider.name,
                nit: newProvider.nit,
            };

            setProviders((prev) => [...prev, formatted]);
            setSelectedProvider(formatted.value);
            setNit(formatted.nit);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const formData = new FormData(formRef.current);
        const payload = Object.fromEntries(formData.entries());

        payload.unit_price = unitPrice;
        payload.quantity = quantity;
        payload.total_purchase_price = totalPurchasePrice;
        payload.sale_price_per_unit = salePrice;
        payload.value_pending = pendingValue;

        if (selectedCategory) {
            payload.category_ids = Array.isArray(selectedCategory)
                ? selectedCategory.map((c) => parseInt(c, 10))
                : [parseInt(selectedCategory, 10)];
        } else {
            payload.category_ids = [];
        }

        const p = Number(unitPrice);
        const q = Number(quantity);
        const s = Number(salePrice);

        if (q <= 0) {
            return setInfoModal({
                iconType: "warning",
                title: "Error de Cantidad",
                description: "La Cantidad debe ser un número entero positivo (mayor a 0).",
                secondaryAction: { label: "Cerrar", onClick: () => setInfoModal(null) },
            });
        }

        if (p <= 0) {
            return setInfoModal({
                iconType: "warning",
                title: "Error de Precio por Unidad",
                description: "El Precio por unidad debe ser un valor positivo.",
                secondaryAction: { label: "Cerrar", onClick: () => setInfoModal(null) },
            });
        }

        if (s < p) {
            return setInfoModal({
                iconType: "warning",
                title: "Error de Precio de Venta",
                description: `El Precio de venta (${s}) debe ser mayor o igual al Precio por unidad (${p}) para asegurar la rentabilidad.`,
                secondaryAction: { label: "Cerrar", onClick: () => setInfoModal(null) },
            });
        }

        const numericFields = ["unit_price", "quantity", "total_purchase_price", "sale_price_per_unit", "min_stock", "value_pending", "value_paid"];
        numericFields.forEach((field) => {
            if (payload[field] !== undefined && payload[field] !== "") {
                payload[field] = parseFloat(Number(payload[field]).toFixed(2));
            }
        });

        setIsLoading(true);

        const url = isEditing
            ? `admin/accounting/product/${productData.productId}/`
            : "admin/accounting/product/create";
        const method = isEditing ? "patch" : "post";

        instanceMob[method](url, payload)
            .then(() => {
                setInfoModal({
                    iconType: "success",
                    title: isEditing
                        ? "Producto actualizado"
                        : "Producto creado y egreso registrado",
                    description: isEditing
                        ? "El producto fue actualizado con éxito."
                        : "El producto ha sido creado exitosamente en el sistema.\n" +
                        "\nAdemás, se ha generado automáticamente el egreso correspondiente a la compra de este producto.\n" +
                        "\nPuedes revisar y verificar esta información en el módulo Ingresos y Egresos.",
                    primaryAction: isEditing
                        ? {
                            label: "Aceptar",
                            onClick: () => {
                                setInfoModal(null);
                                onClose(true);
                            },
                        }
                        : {
                            label: "Ver Ingresos y Egresos",
                            onClick: () => {
                                setInfoModal(null);
                                onClose(true);
                                navigate("/accounting/incomes-expenses");
                            },
                        },
                    secondaryAction: !isEditing
                        ? {
                            label: "Aceptar",
                            onClick: () => {
                                setInfoModal(null);
                                onClose(true);
                            },
                        }
                        : null,
                });
            })
            .catch((err) => {
                const errorMessage = getBackendError(err);
                setInfoModal({
                    iconType: "error",
                    title: "Error",
                    description: errorMessage,
                    secondaryAction: { label: "Cerrar", onClick: () => setInfoModal(null) },
                });
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        if (show) fetchCategoriesBrandsAndProviders();
    }, [show]);

    useEffect(() => {
        if (!productData || !categories.length || !brands.length || !providers.length) return;

        if (isInitialized) return;

        const foundProvider = providers.find(
            (p) => p.label.toLowerCase() === productData?.provider?.toLowerCase()
        );
        if (foundProvider) {
            setSelectedProvider(foundProvider.value);
            setNit(foundProvider.nit || "");
        }

        const foundCategories = categories
            .filter((c) =>
                productData.categories?.some(
                    (cat) => cat.toLowerCase() === c.label.toLowerCase()
                )
            )
            .map((c) => c.value);
        setSelectedCategory(foundCategories);

        const foundBrand = brands.find(
            (b) => b.label.toLowerCase() === productData.brand?.toLowerCase()
        );
        setSelectedBrand(foundBrand ? foundBrand.value : null);

        setSalePrice(productData.sale_price_per_unit?.toString() || "");
        setQuantity(productData.quantity?.toString() || "");
        setUnitPrice(productData.unitPrice?.toString() || "");
        setTotalPurchasePrice(productData.total_purchase_price?.toString() || "");
        setPaidValue(productData.value_paid || 0);
        setPendingValue(productData.value_pending || 0);
        setPaymentMethod(productData.payment_method || "");

        setIsInitialized(true);
    }, [productData, categories, brands, providers, isInitialized]);

    useEffect(() => {
        if (isBlocked) return;

        const numericUnitPrice = Number(unitPrice) || 0;
        const numericQuantity = isEditing ? Number(newQuantity) || 0 : Number(quantity) || 0;
        const numericPaidValue = Number(paidValue) || 0;

        if (numericUnitPrice <= 0 || numericQuantity <= 0) {
            setTotalPurchasePrice(0);
            setPendingValue(0);
            return;
        }

        const total = (numericUnitPrice * numericQuantity);
        const pending = Math.max(Number(total) - numericPaidValue, 0);

        setTotalPurchasePrice(total);
        setPendingValue(pending);
    }, [unitPrice, quantity, newQuantity, paidValue, isEditing, isBlocked]);

    return (
        <>
            <BaseModal
                show={show}
                onClose={onClose}
                title={isEditing ? "Editar producto" : "Registro de producto"}
                onSave={() => formRef.current.requestSubmit()}
                showCancelButton={true}
                saveText={isEditing ? "Guardar cambios" : "Registrar producto"}
            >
                <form className="d-flex flex-column gap-2" ref={formRef} onSubmit={handleSubmit}>
                    {formFields.map((field, index) => {
                        const FieldComponent =
                            typeof field.Component === "string"
                                ? React.createElement(field.Component, { ...field })
                                : <field.Component {...field} />;
                        const isHidden = field.type === "hidden";
                        if (!isHidden && field.tooltip) {
                            return (
                                <Tooltip
                                    key={index}
                                    title={field.label}
                                    description={field.tooltip}
                                    position="right"
                                    variant="rich"
                                >
                                    {FieldComponent}
                                </Tooltip>
                            );
                        }
                        return <React.Fragment key={index}>{FieldComponent}</React.Fragment>;
                    })}
                    <input type="hidden" name="other_payment_method"/>
                    <input type="hidden" name="location_id" value={selectedLocation?.id || ""}/>
                </form>
            </BaseModal>

            <CreateGenericItemModal
                show={showCreateItemModal}
                onClose={handleCloseCreateItemModal}
                itemType={itemTypeToCreate}
            />

            <CreateProviderModal
                show={showCreateProviderModal}
                onClose={handleCloseCreateProviderModal}
            />

            {infoModal && (
                <InlineModal
                    icon={infoModal.icon}
                    iconType={infoModal.iconType}
                    title={infoModal.title}
                    description={infoModal.description}
                    primaryAction={infoModal.primaryAction}
                    secondaryAction={infoModal.secondaryAction}
                />
            )}
        </>
    );
};

export default ProductRegistrationModal;